import React, { useState, useEffect } from 'react';
import { Checkpoint, Section } from '../../types';
import { enhancedApiService } from '../../services/enhancedApiService';
import { useToast } from '../ui/Toast';
import { permissionChecks } from '../../lib/auth/helpers';

export const SectionsManagement: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [viewingSection, setViewingSection] = useState<Section | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkpointsLoading, setCheckpointsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    checkpoint_id: '',
    section_name: '',
    description: ''
  });
  
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const { showToast } = useToast();

  useEffect(() => {
    loadSections();
    loadCheckpoints();
  }, []);

  const loadSections = async () => {
    try {
      setLoading(true);
      const allSections = await enhancedApiService.getSections();
      setSections(allSections);
    } catch (error) {
      console.error('Failed to load sections:', error);
      showToast('Failed to load sections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCheckpoints = async () => {
    try {
      setCheckpointsLoading(true);
      const allCheckpoints = await enhancedApiService.getCheckpoints();
      // Only show active checkpoints
      setCheckpoints(allCheckpoints.filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to load checkpoints:', error);
      showToast('Failed to load checkpoints', 'error');
    } finally {
      setCheckpointsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.checkpoint_id) {
      errors.checkpoint_id = 'Please select a checkpoint';
    }
    
    if (!formData.section_name.trim()) {
      errors.section_name = 'Section name is required';
    } else if (formData.section_name.trim().length < 2) {
      errors.section_name = 'Section name must be at least 2 characters';
    } else if (formData.section_name.trim().length > 100) {
      errors.section_name = 'Section name must be less than 100 characters';
    }
    
    if (formData.description && formData.description.trim().length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permissionChecks.canUpdateSections()) {
      showToast('You do not have permission to create or update sections', 'error');
      return;
    }
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (editingSection) {
        await enhancedApiService.updateSection(editingSection.section_id, {
          checkpoint_id: parseInt(formData.checkpoint_id),
          section_name: formData.section_name,
          description: formData.description.trim() || undefined
        });
        showToast('Section updated successfully!', 'success');
      } else {
        await enhancedApiService.createSection({
          checkpoint_id: parseInt(formData.checkpoint_id),
          section_name: formData.section_name,
          description: formData.description.trim() || undefined
        });
        showToast('Section created successfully!', 'success');
      }
      
      resetForm();
      await loadSections();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save section';
      setFormErrors({ submit: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (section: Section) => {
    if (!permissionChecks.canUpdateSections()) {
      showToast('You do not have permission to edit sections', 'error');
      return;
    }
    setEditingSection(section);
    setFormData({
      checkpoint_id: section.checkpoint_id.toString(),
      section_name: section.section_name,
      description: section.description || ''
    });
    setShowAddForm(true);
    setFormErrors({});
  };

  const handleToggleStatus = async (section: Section) => {
    if (!permissionChecks.canUpdateSections()) {
      showToast('You do not have permission to toggle section status', 'error');
      return;
    }
    if (loading) return;
    
    setLoading(true);
    try {
      // Toggle section status
      
      // Call the toggle API endpoint
      const result = await enhancedApiService.toggleSection(section.section_id, true, 'admin');
              // Toggle result received
      
      showToast(`Section ${!section.isOpen ? 'opened' : 'closed'} successfully!`, 'success');
      
      // Wait a bit for the backend to process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force refresh the sections list by bypassing cache
              // Refreshing sections with force refresh
      const allSections = await enhancedApiService.getSections(undefined, true);
              // Refreshed sections loaded
      setSections(allSections);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle section status';
      console.error('Toggle error:', error);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (section: Section) => {
    setViewingSection(section);
  };

  const handleDelete = async (section: Section) => {
    if (!permissionChecks.canUpdateSections()) {
      showToast('You do not have permission to delete sections', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${section.section_name}"?`)) {
      return;
    }
    
    setLoading(true);
    try {
      await enhancedApiService.deleteSection(section.section_id);
      showToast('Section deleted successfully!', 'success');
      await loadSections();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete section';
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      checkpoint_id: '',
      section_name: '',
      description: ''
    });
    setFormErrors({});
    setShowAddForm(false);
    setEditingSection(null);
  };

  const getCheckpointName = (checkpointId: number) => {
    const checkpoint = (Array.isArray(checkpoints) ? checkpoints : []).find(c => c.checkpoint_id === checkpointId);
    return checkpoint ? checkpoint.checkpoint_name : `Checkpoint ${checkpointId}`;
  };

  const activeSections = (Array.isArray(sections) ? sections : []).filter(section => section.is_active);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Sections Management</h1>
          <p className="text-gray-400 text-sm">Organize terminal sections</p>
        </div>
        {permissionChecks.canUpdateSections() && (
          <button 
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors text-sm font-medium"
          >
            {showAddForm ? 'Cancel' : 'Add Section'}
          </button>
        )}
      </div>
      
      {/* Add/Edit Section Form */}
      {showAddForm && (
        <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingSection ? 'Edit Section' : 'Add New Section'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Checkpoint Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Checkpoint <span className="text-red-400">*</span>
              </label>
              <select 
                name="checkpoint_id"
                value={formData.checkpoint_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.checkpoint_id ? 'border-red-500' : 'border-gray-600'
                }`}
                disabled={loading || checkpointsLoading}
              >
                <option value="">Select a checkpoint</option>
                {checkpoints.map((checkpoint) => (
                  <option key={checkpoint.checkpoint_id} value={checkpoint.checkpoint_id}>
                    {checkpoint.checkpoint_name}
                  </option>
                ))}
              </select>
              {formErrors.checkpoint_id && (
                <p className="mt-1 text-sm text-red-400">{formErrors.checkpoint_id}</p>
              )}
              {checkpointsLoading && (
                <p className="mt-1 text-xs text-gray-400">Loading checkpoints...</p>
              )}
            </div>

            {/* Section Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Section Name <span className="text-red-400">*</span>
              </label>
              <input 
                type="text" 
                name="section_name"
                value={formData.section_name}
                onChange={handleInputChange}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formErrors.section_name ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter section name (e.g., Security Area A, Baggage Zone)" 
                disabled={loading}
              />
              {formErrors.section_name && (
                <p className="mt-1 text-sm text-red-400">{formErrors.section_name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea 
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className={`w-full px-3 py-3 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                  formErrors.description ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter section description (optional)" 
                disabled={loading}
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-400">{formErrors.description}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">Maximum 500 characters</p>
            </div>

            {/* Submit Error */}
            {formErrors.submit && (
              <div className="p-3 bg-red-900/50 border border-red-500 rounded-md">
                <p className="text-sm text-red-400">{formErrors.submit}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit" 
                disabled={loading}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                {loading ? (editingSection ? 'Updating...' : 'Creating...') : (editingSection ? 'Update Section' : 'Create Section')}
              </button>
              <button 
                type="button" 
                onClick={resetForm}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Section List */}
      <div className="bg-gray-800 rounded-xl p-4 sm:p-6">
        <h3 className="text-lg font-semibold mb-4">Section List</h3>
        {loading && !showAddForm ? (
          <div className="text-center py-8">
            <div className="loading-spinner mx-auto mb-2"></div>
            <p className="text-gray-400">Loading sections...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeSections.map((section) => (
              <div key={section.section_id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-gray-700 rounded space-y-2 sm:space-y-0">
                <div className="flex-1">
                  <div className="font-medium text-white">{section.section_name}</div>
                  <div className="text-sm text-blue-300 font-mono">ID: {section.section_id}</div>
                  <div className="text-sm text-gray-400">
                    Checkpoint: {getCheckpointName(section.checkpoint_id)}
                  </div>
                  {section.description && (
                    <div className="text-sm text-gray-400 mt-1">{section.description}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Created: {new Date(section.created_date).toLocaleDateString()}
                  </div>
                </div>
                {permissionChecks.canUpdateSections() ? (
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Status Toggle */}
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-400">Open</span>
                      <button
                        onClick={() => handleToggleStatus(section)}
                        disabled={loading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                          loading
                            ? 'bg-gray-500 cursor-not-allowed'
                            : section.isOpen 
                            ? 'bg-green-600 focus:ring-green-500' 
                            : 'bg-gray-600 focus:ring-gray-500'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            section.isOpen ? 'translate-x-6' : 'translate-x-1'
                          } ${loading ? 'animate-pulse' : ''}`}
                        />
                      </button>
                      <span className={`text-xs font-medium ${section.isOpen ? 'text-green-400' : 'text-gray-400'}`}>
                        {section.isOpen ? 'Open' : 'Closed'}
                      </span>
                    </div>
                    
                    {/* Edit Button */}
                    <button 
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                      onClick={() => handleEdit(section)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-400">Open</span>
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        section.isOpen ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                      <span className="text-sm text-gray-400">
                        {section.isOpen ? 'Open' : 'Closed'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {activeSections.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-400">
                No sections found. Create your first section to get started.
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Section Modal */}
      {viewingSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Section Details</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-400">Name</label>
                <p className="text-white">{viewingSection.section_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Checkpoint</label>
                <p className="text-white">{getCheckpointName(viewingSection.checkpoint_id)}</p>
              </div>
              {viewingSection.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-400">Description</label>
                  <p className="text-white">{viewingSection.description}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-400">Status</label>
                <p className="text-green-400">Active</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Created Date</label>
                <p className="text-white">
                  {new Date(viewingSection.created_date).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">Created</label>
                <p className="text-white">{new Date(viewingSection.created_date).toLocaleString()}</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => setViewingSection(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
